import React, { useState, useEffect } from "react";
import { Button, Card, Form, ListGroup } from "react-bootstrap";
import "./style.css";
import { getSummarymessages, postSummarymessages } from "../../../services/report.api";
import Loader from "../../Loader/loader.components";

// Utility function to decode JWT token
const decodeJWT = (token: string) => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const decodedData = JSON.parse(atob(base64));
  return decodedData;
};

const Messages: React.FC<{
  sidebyside: boolean;
  disabled: boolean;
  reportId: string;
  recipientUser: string;
  projectId: string;
}> = ({ sidebyside, disabled, reportId, recipientUser, projectId }) => {
  const [activities, setActivities] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");  // Local message state

  useEffect(() => {
    fetchActivities();
  }, [reportId]);

  const fetchActivities = async () => {
    setIsLoading(true);
    try {
      const response = await getSummarymessages(reportId);
      setActivities(response.messages);
    } catch (error) {
      console.error("Error fetching activities:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return; // Don't submit empty messages

    setIsLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('Access token not found in localStorage');
      }

      const decodedToken = decodeJWT(token);
      const senderContact = decodedToken.salesforceId;

      const messagePayload = {
        messageBody: message,
        senderContact: senderContact,
        SummaryReportId: reportId,
        recipientUser: recipientUser,
        logoRequest: "",
        projectId: projectId,
      };

      const response = await postSummarymessages(messagePayload);
      console.log("Message submitted successfully!", response);

      // Refresh the activity list after successful post
      fetchActivities();

      // Clear the textarea
      setMessage("");
    } catch (error) {
      console.error("Error submitting message:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const decodeHTML = (text: string) => {
    const doc = new DOMParser().parseFromString(text, "text/html");
    return doc.documentElement.textContent || "";
  };

  return (
    <Card className="p-3" style={{ borderRadius: "12px", boxShadow: "0px 0px 4px 0px #00000026" }}>
      {isLoading && <Loader />}

      <div className={sidebyside ? "d-flex flex-column flex-md-row justify-content-between" : ""}>
        <div className={sidebyside ? "mb-4 mb-md-0" : ""} style={sidebyside ? { width: "100%", maxWidth: "48%" } : {}}>
          <h2 className="new-message-title">New Message</h2>
          <Form.Label className="new-message-sublabel">
            Use this form to send notes or follow-ups to your Technical Reviewer.
          </Form.Label>
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="newMessage">
              <Form.Control
                as="textarea"
                rows={5}
                placeholder="Add text..."
                className="new-message-textarea"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={disabled || isLoading}
              />
            </Form.Group>
            <div className="d-flex justify-content-end mt-3">
              <Button
                variant="success"
                className="px-5 py-2 rounded-pill submit-btn"
                type="submit"
                disabled={disabled || isLoading || !message.trim()}
              >
                Submit
              </Button>
            </div>
          </Form>
        </div>

        <div style={sidebyside ? { width: "100%", maxWidth: "48%" } : {}} className="mt-5">
          <div className="activity-container">
            <h3 className="activity-title">Activity</h3>
            <div className="activity-timeline">
              <div className="timeline-line"></div>

              <ListGroup className="activityContent">
                {activities.map((activity, index) => (
                  <div key={activity.id} className={`timeline-item ${index % 2 === 0 ? "left" : "right"}`}>
                    <span className="timeline-point"></span>
                    <ListGroup.Item className="activityUser-heading-action">
                      <strong>{activity.Message_Shared_By__c.replace(/^Auditor:\s*/, "")}</strong>
                      <span className="activity-timestamp">
                        {new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(new Date(activity.LastModifiedDate))}
                      </span>
                      {activity.Message_Body__c && (
                        <p
                          className="activity-heading-sublabel"
                          dangerouslySetInnerHTML={{
                            __html: new DOMParser()
                              .parseFromString(activity.Message_Body__c, "text/html")
                              .documentElement.textContent // Decodes all entities safely
                              ?.replace(/“|”/g, '"') // Convert fancy quotes to standard quotes
                              ?.replace(/‘|’/g, "'") || "", // Convert fancy apostrophes to standard
                          }}
                        ></p>
                      )}
                    </ListGroup.Item>
                  </div>
                ))}
              </ListGroup>

            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default Messages;
